/* Coordinate the observed open shadow roots without changing consent or app settings. */
(() => {
  if (window.__beleaevOverlayCoordinator) return;
  window.__beleaevOverlayCoordinator = true;
  const hosts = new WeakMap();
  const states = new WeakSet();
  const waiting = new WeakSet();
  const stateSelector = 'product-modal, quick-add-modal, cart-drawer, .beleaev-sticky-atc, .mobile-facets__disclosure, dialog.blv-serving-guide-dialog, header-drawer > details';
  const headerSelector = '.section-header, sticky-header, .header-wrapper, .announcement-bar, .announcement-bar-section';
  let headers = [];
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => { frame = 0; sync(); });
  };
  const headerResize = new ResizeObserver(schedule);
  const consentResize = new ResizeObserver(schedule);
  let measuredNotice = null;
  const headerMutation = new MutationObserver(schedule);
  const visible = (element) => {
    if (!element || !element.getClientRects().length) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };
  function sync() {
    const consent = document.querySelector('pandectes-cmp')?.shadowRoot?.querySelector('dialog[open]');
    const notice = document.querySelector('pandectes-cmp')?.shadowRoot?.querySelector('dialog[open].notice.bar.bottom');
    if (notice !== measuredNotice) {
      consentResize.disconnect();
      measuredNotice = notice;
      if (notice) consentResize.observe(notice);
    }
    // Reserve the banner's occupied bottom area without reading or changing consent.
    const drawer = document.querySelector('cart-drawer');
    const pane = document.getElementById('CartDrawer');
    const viewportTop = window.visualViewport?.offsetTop || 0;
    const viewportBottom = Math.min(innerHeight, viewportTop + (window.visualViewport?.height || innerHeight));
    let inset = 0;
    if (drawer && pane && visible(notice)) {
      const bannerRect = notice.getBoundingClientRect();
      const drawerRect = drawer.getBoundingClientRect();
      const paneRect = pane.getBoundingClientRect();
      const overlaps = bannerRect.height > 0 && bannerRect.width > 0 &&
        bannerRect.bottom > viewportTop && bannerRect.top < viewportBottom &&
        bannerRect.right > paneRect.left && bannerRect.left < paneRect.right;
      if (overlaps && drawerRect.height > 0 && bannerRect.top < drawerRect.bottom) {
        // Measure the unchanged outer drawer, so resizing its inner pane cannot feed back.
        inset = Math.min(Math.ceil(drawerRect.height), Math.ceil(drawerRect.bottom - Math.max(drawerRect.top, bannerRect.top)) + 8);
      }
    }
    const insetValue = `${inset}px`;
    if (document.documentElement.style.getPropertyValue('--beleaev-cart-consent-inset') !== insetValue) {
      document.documentElement.style.setProperty('--beleaev-cart-consent-inset', insetValue);
    }
    document.documentElement.toggleAttribute('data-beleaev-cart-consent-inset', inset > 0);
    const busy = !!document.querySelector('product-modal[open], quick-add-modal[open], cart-drawer.active, .mobile-facets__disclosure[open], dialog.blv-serving-guide-dialog[open], header-drawer > details[open]');
    // Move the reopen button above occupied mobile purchase/footer regions; keep it usable.
    const consentRoot = document.querySelector('pandectes-cmp')?.shadowRoot;
    if (consentRoot) {
      let reopenStyle = consentRoot.querySelector('style[data-beleaev-reopen-offset]');
      if (!reopenStyle) {
        reopenStyle = document.createElement('style');
        reopenStyle.dataset.beleaevReopenOffset = '';
        consentRoot.append(reopenStyle);
      }
      let bottom = 16;
      if (innerWidth <= 749) {
        const regions = [
          document.querySelector('cart-drawer.active .drawer__footer'),
          document.querySelector('.beleaev-sticky-atc--show'),
        ];
        regions.forEach(region => {
          if (!visible(region)) return;
          const rect = region.getBoundingClientRect();
          if (rect.top < viewportBottom && rect.bottom > viewportTop) {
            bottom = Math.max(bottom, Math.ceil(viewportBottom - rect.top) + 12);
          }
        });
      }
      const css = `button.widget.bottom { bottom:${bottom}px !important; }`;
      if (reopenStyle.textContent !== css) reopenStyle.textContent = css;
    }
    const headerBottom = headers.reduce((bottom, header) => {
      if (!header.isConnected || !visible(header)) return bottom;
      const rect = header.getBoundingClientRect();
      return rect.top < innerHeight && rect.bottom > 0 ? Math.max(bottom, Math.min(innerHeight, rect.bottom)) : bottom;
    }, 0);
    const teaserTop = Math.ceil(headerBottom) + 12;
    document.querySelectorAll('shopify-forms-embed').forEach((host) => {
      const root = host.shadowRoot;
      if (!root) return;
      let style = root.querySelector('style[data-beleaev-overlay]');
      if (!style) {
        style = document.createElement('style');
        style.dataset.beleaevOverlay = '';
        root.append(style);
      }
      // Keep app state intact so the same form/teaser is restored after consent or purchase UI.
      const blocked = visible(consent) || busy;
      const css = blocked ? '[data-form-root][data-current-step="form"], [data-form-root][data-current-step="tease"] { display:none !important; }' : `[data-form-root][data-current-step="tease"] { top:${teaserTop}px !important; bottom:auto !important; left:12px !important; max-width:calc(100vw - 24px) !important; }`;
      if (style.textContent !== css) style.textContent = css;
    });
  }
  function attach() {
    headers = [...document.querySelectorAll(headerSelector)];
    headerResize.disconnect();
    headerMutation.disconnect();
    headers.forEach(header => {
      headerResize.observe(header);
      headerMutation.observe(header, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
    });
    document.querySelectorAll('shopify-forms-embed, pandectes-cmp').forEach((host) => {
      const root = host.shadowRoot;
      if (!root && !waiting.has(host)) {
        waiting.add(host);
        [100, 500, 1500, 4000].forEach(delay => setTimeout(attach, delay));
      }
      if (!root || hosts.has(root)) return;
      const observer = new MutationObserver(schedule);
      observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'style', 'class', 'hidden', 'aria-hidden', 'data-current-step'] });
      root.addEventListener('transitionend', schedule);
      root.addEventListener('animationend', schedule);
      hosts.set(root, observer);
    });
    document.querySelectorAll(stateSelector).forEach(element => {
      if (element.matches('cart-drawer')) headerResize.observe(element);
      if (states.has(element)) return;
      states.add(element);
      new MutationObserver(schedule).observe(element, { attributes: true, attributeFilter: ['open', 'class'] });
    });
    sync();
  }
  function start() {
    attach();
    // Only mounts/removals need discovery; shadow state is observed at its owner.
    new MutationObserver((records) => {
      const discoverySelector = 'shopify-forms-embed, pandectes-cmp, ' + stateSelector + ', ' + headerSelector;
      if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 &&
          (node.matches(discoverySelector) || node.querySelector(discoverySelector))))) attach();
    }).observe(document.body, { childList: true, subtree: true });
    ['shopify-forms-embed', 'pandectes-cmp'].forEach((name) => customElements.whenDefined(name).then(() => {
      attach();
      // Apps may create their shadow root asynchronously after upgrading the host.
      [100, 500, 1500, 4000].forEach((delay) => setTimeout(attach, delay));
    }));
    document.addEventListener('toggle', attach, true);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true });
    window.visualViewport?.addEventListener('scroll', schedule, { passive: true });
    document.addEventListener('transitionend', event => {
      if (event.target.closest(headerSelector + ', cart-drawer, .beleaev-sticky-atc')) schedule();
    }, true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();


